import type {
  AvailabilityGridData,
  AvailabilityGridRow,
  AvailabilityPeriod,
  CellState,
  MultiSelectCellRef,
} from '../lib/availability-types';

/**
 * The SELECT-eligible cell-state predicate per the `unified-availability-interaction`
 * model — governs which cells a LONE click opens the unified SELECT popover for. Exported
 * so every lone-click gate (this grid's own `handleMultiCellClick`, the offer merged
 * table's `useMultiSelectClick`, and the catalog route's own lone-click handler) shares
 * the exact same narrow set instead of re-deriving it. Deliberately narrower than
 * `isMultiSelectEligibleState` below — see that function's doc comment for why the two
 * sets diverge (`reservation-mixed-range-and-target-expiry`, task 4.1).
 */
export function isShiftSelectableState(cellState: CellState): boolean {
  return (
    cellState === 'free' ||
    cellState === 'option-own' ||
    cellState === 'reserved-own-soft' ||
    cellState === 'reserved-own-hard'
  );
}

/**
 * The MULTI-SELECT-ELIGIBLE cell-state predicate — governs which cells a drag or
 * shift-click RECTANGLE may contain, per the `unified-availability-interaction` spec's
 * "Multi-select opt-in on the shared availability grid" requirement. Wider than
 * `isShiftSelectableState`: `reserved-other-soft`/`reserved-other-hard` and `option-other`
 * are included because a caller dragging across a run of periods routinely covers periods a
 * colleague already holds, and those are exactly the periods they need to queue for or (for
 * `option-other`) can outright reserve per the displacement matrix (CU-86cbc4t4q). A cell
 * being multi-select-eligible does NOT make it independently clickable — a LONE click on an
 * `option-other`/`reserved-other-soft`/`reserved-other-hard` cell still opens the read-only
 * INSPECT popover, gated
 * separately by `isShiftSelectableState`. Only `sold` and availability-window-blocked
 * cells are excluded, unchanged from before.
 */
export function isMultiSelectEligibleState(cellState: CellState): boolean {
  return (
    cellState === 'free' ||
    cellState === 'option-own' ||
    cellState === 'reserved-own-soft' ||
    cellState === 'reserved-own-hard' ||
    cellState === 'option-other' ||
    cellState === 'reserved-other-soft' ||
    cellState === 'reserved-other-hard'
  );
}

/**
 * Computes the rectangular set of (unitId, periodId) cells between two corner cells
 * (inclusive on both dimensions), filtered to MULTI-SELECT-ELIGIBLE cell states. Used by
 * the `multiSelect` drag / shift-click-fill rectangle-selection mechanics — NOT by a lone
 * (non-shift) click, which uses the narrower `isShiftSelectableState` gate instead.
 *
 * Exported so callers that don't render `AvailabilityGrid` at all (e.g. the inventory
 * catalog's `DataTable`+`RichAvailabilityCell` pipeline, `unify-availability-interaction`
 * task 4.5) can reuse the exact same geometry algorithm instead of re-deriving it —
 * the function only depends on `row.id`/`period.id` shape, not on any grid-internal state.
 */
// eslint-disable-next-line complexity -- nested-loop rectangle scan with two undefined guards; extraction would split a cohesive geometry helper.
export function computeMultiSelectRectangle(
  rows: AvailabilityGridRow[],
  periods: AvailabilityPeriod[],
  grid: AvailabilityGridData,
  cornerA: MultiSelectCellRef,
  cornerB: MultiSelectCellRef,
): MultiSelectCellRef[] {
  const rowIndexA = rows.findIndex((r) => r.id === cornerA.unitId);
  const rowIndexB = rows.findIndex((r) => r.id === cornerB.unitId);
  const colIndexA = periods.findIndex((p) => p.id === cornerA.periodId);
  const colIndexB = periods.findIndex((p) => p.id === cornerB.periodId);
  if (rowIndexA === -1 || rowIndexB === -1 || colIndexA === -1 || colIndexB === -1) {
    return [];
  }

  const rowStart = Math.min(rowIndexA, rowIndexB);
  const rowEnd = Math.max(rowIndexA, rowIndexB);
  const colStart = Math.min(colIndexA, colIndexB);
  const colEnd = Math.max(colIndexA, colIndexB);

  const cells: MultiSelectCellRef[] = [];
  for (let r = rowStart; r <= rowEnd; r += 1) {
    const row = rows[r];
    if (!row) continue;
    for (let c = colStart; c <= colEnd; c += 1) {
      const period = periods[c];
      if (!period) continue;
      const cellState = grid[row.id]?.[period.id] ?? 'free';
      if (isMultiSelectEligibleState(cellState)) {
        cells.push({ unitId: row.id, periodId: period.id });
      }
    }
  }
  return cells;
}
